// Normalises any thrown value into a typed `AppError`. The two axios
// instances in `httpClient.ts` run their response interceptors through this
// function so feature code can rely on `catch (e) { … }` always producing
// the same shape.
//
// The fallback message must never be a user-facing surprise — it appears
// when the wire format is unrecognised, so keep it generic and benign.

import axios, { type AxiosError } from 'axios'

export interface AppError {
  message: string
  statusCode?: number
  // Backend `errorCode` field (e.g. `PROPERTY_OWNER_EMAIL_TAKEN`) — lets
  // containers route specific errors to specific surfaces (inline vs banner)
  // without matching against localized messages.
  errorCode?: string
  details?: unknown
}

const DEFAULT_ERROR_MESSAGE = 'An unexpected error occurred.'

// A gateway 502/503/504 (e.g. mid-deploy) usually carries no CORS headers, so
// the browser hides the status and axios reports a bare `ERR_NETWORK` with no
// response. Both shapes mean "the service is unreachable right now" — surface
// that instead of axios's raw 'Network Error'.
export const SERVICE_UNAVAILABLE_ERROR_CODE = 'SERVICE_UNAVAILABLE'
const SERVICE_UNAVAILABLE_MESSAGE = 'Service temporarily unavailable. Please try again in a moment.'
const REQUEST_TIMEOUT_MESSAGE = 'The request timed out. Please try again.'
const GATEWAY_ERROR_STATUSES = new Set([502, 503, 504])

export const isAppError = (error: unknown): error is AppError =>
  !!error &&
  typeof error === 'object' &&
  !axios.isAxiosError(error) &&
  !(error instanceof Error) &&
  'message' in error &&
  typeof (error as { message: unknown }).message === 'string'

// Pulls the most caller-friendly message out of an axios error: API body
// `message` / `error` field first, then axios's own message, then the
// generic fallback. Extracted so `normalizeError` stays at one branch per
// error kind.
const extractAxiosMessage = (error: AxiosError): string => {
  const data = error.response?.data
  if (data && typeof data === 'object') {
    if ('message' in data && typeof data.message === 'string') return data.message
    if ('error' in data && typeof data.error === 'string') return data.error
  }
  return error.message || DEFAULT_ERROR_MESSAGE
}

const extractAxiosErrorCode = (error: AxiosError): string | undefined => {
  const data = error.response?.data
  if (data && typeof data === 'object' && 'errorCode' in data) {
    const value = (data as { errorCode: unknown }).errorCode
    if (typeof value === 'string') return value
  }
  return undefined
}

const normalizeUnreachableError = (error: AxiosError): AppError | null => {
  if (error.code === 'ECONNABORTED') return { message: REQUEST_TIMEOUT_MESSAGE }
  const status = error.response?.status
  const isNetworkFailure = !error.response && error.code === 'ERR_NETWORK'
  if (!isNetworkFailure && !(status && GATEWAY_ERROR_STATUSES.has(status))) return null
  return {
    message: SERVICE_UNAVAILABLE_MESSAGE,
    statusCode: status,
    errorCode: SERVICE_UNAVAILABLE_ERROR_CODE,
    details: error.response?.data,
  }
}

export const normalizeError = (error: unknown): AppError => {
  if (isAppError(error)) return error

  if (axios.isAxiosError(error)) {
    const unreachable = normalizeUnreachableError(error)
    if (unreachable) return unreachable
    return {
      message: extractAxiosMessage(error),
      statusCode: error.response?.status,
      errorCode: extractAxiosErrorCode(error),
      details: error.response?.data,
    }
  }

  if (error instanceof Error) {
    return { message: error.message }
  }

  return { message: DEFAULT_ERROR_MESSAGE }
}

// Pulls the specific cause out of an error body's `errors` field. Services that
// proxy an upstream (admin-service → vaisp-gateway → Ctrl-Alt) answer with a
// generic `message` plus the actionable sentence in `errors` — as a string on a
// vendor failure, or as a field-error array on a validation failure.
const readErrorDetail = (error: AppError): string | null => {
  if (!error.details || typeof error.details !== 'object') return null
  const { errors } = error.details as { errors?: unknown }
  if (typeof errors === 'string') return errors.trim() || null
  if (!Array.isArray(errors)) return null
  const lines = errors
    .map((entry) => {
      if (typeof entry === 'string') return entry
      const message = (entry as { message?: unknown })?.message
      return typeof message === 'string' ? message : ''
    })
    .filter(Boolean)
  return lines.length ? lines.join(' · ') : null
}

// Operator-facing message for a caught error. The axios interceptor rejects with
// a plain-object `AppError`, never an `Error` instance, so an `instanceof Error`
// check silently swallows every server message and always falls through to the
// caller's fallback — use this instead. `fallback` covers only the case where the
// wire shape carried no usable message of its own.
export const toErrorMessage = (error: unknown, fallback: string): string => {
  const appError = normalizeError(error)
  const base =
    appError.message && appError.message !== DEFAULT_ERROR_MESSAGE ? appError.message : fallback
  const detail = readErrorDetail(appError)
  return detail && detail !== base ? `${base} — ${detail}` : base
}
